/**
 * MyCopro - Hook usePushNotifications
 * Initialise les push notifications au montage quand l'utilisateur est authentifié.
 * 
 * RESPONSABILITÉS:
 * - Enregistrer le FCM token au login (ou au montage si déjà authentifié)
 * - Écouter les push reçus au premier plan → afficher un toast in-app
 * - Gérer la navigation au clic sur la notification (foreground)
 * - Se désinscrire au démontage / logout
 * 
 * ✅ FIX: Attendre que residenceId soit disponible avant d'enregistrer le token
 *    pour éviter la création de doublons (un avec residenceId=null, un avec la valeur).
 *    Les admin/syndic n'ont pas de residenceId → ils sont enregistrés sans.
 * 
 * UTILISATION:
 * Appeler une seule fois dans App.tsx ou dans un composant wrapper authentifié:
 *   usePushNotifications();
 * 
 * src/hooks/usePushNotifications.ts
 */

import { useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { useAuthStore, useResidenceStore, useUIStore } from '@/stores';
import {
  registerPushNotifications,
  unregisterPushNotifications,
  initForegroundListener,
  ForegroundMessage,
} from '@/services/firebase/messaging';
import { logger } from '@/utils/logger';

export const usePushNotifications = () => {
  const navigate = useNavigate();

  // ============================================
  // STATE — Stores
  // ============================================
  const currentUser = useAuthStore((state) => state.currentUser);
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const currentResidence = useResidenceStore((state) => state.currentResidence);
  const { showToast } = useUIStore();

  // ============================================
  // REFS — Éviter les ré-enregistrements inutiles
  // ============================================
  const isRegistered = useRef(false);
  const registeredTokenHash = useRef<string | null>(null);
  const cleanupForeground = useRef<(() => void) | null>(null);

  // ============================================
  // IDs extraits pour les dépendances
  // ============================================
  const userId = currentUser?.id || currentUser?.uid;
  const userCategory = currentUser?.category;
  const residenceId = currentResidence?.id;

  // ============================================
  // ✅ FIX: Déterminer si on est prêt à enregistrer
  // Les résidents doivent attendre que residenceId soit chargé.
  // Les admin/syndic n'ont pas de résidence → prêts immédiatement.
  // ============================================
  const isAdminOrSyndic = userCategory === 'admin' || userCategory === 'syndic';
  const isReadyToRegister = isAuthenticated && !!userId && (isAdminOrSyndic || !!residenceId);

  // ============================================
  // CALLBACK — Réception d'un message au premier plan
  // ============================================
  const handleForegroundMessage = useCallback(
    (message: ForegroundMessage) => {
      logger.push('Message foreground:', message);

      // Ne pas afficher le toast si l'utilisateur est déjà dans une conversation chat —
      // il voit les messages en temps réel via le listener Firestore.
      const isInChat = window.location.pathname.startsWith('/chat/');
      if (isInChat) return;

      showToast(`${message.title}: ${message.body}`, 'info');
    },
    [showToast]
  );

  // ============================================
  // EFFET PRINCIPAL — Enregistrement au login
  // ✅ FIX: Ne s'exécute qu'une fois quand TOUTES les conditions sont réunies
  // ============================================
  useEffect(() => {
    // Attendre que toutes les conditions soient remplies
    if (!isReadyToRegister) {
      return;
    }

    // Éviter les ré-enregistrements dans la même session
    if (isRegistered.current) {
      return;
    }

    let isCancelled = false;

    const initPush = async () => {
      try {
        logger.push('Initialisation push pour user:', userId, 'résidence:', residenceId || 'aucune');

        // 0. Créer le canal de notification Android (requis pour Android 8+)
        if (Capacitor.getPlatform() === 'android') {
          const { PushNotifications } = await import('@capacitor/push-notifications');
          await PushNotifications.createChannel({
            id: 'ocopro_notifications',
            name: "Notifications O'Copro",
            description: 'Notifications de la résidence',
            importance: 4, // IMPORTANCE_HIGH
            visibility: 1, // VISIBILITY_PUBLIC
            vibration: true,
          });
        }

        // 1. Enregistrer et récupérer le token
        const token = await registerPushNotifications(userId!, residenceId);

        if (isCancelled) return;

        if (token) {
          isRegistered.current = true;
          registeredTokenHash.current = token.substring(0, 20);
          logger.push('Push enregistré avec succès');
        } else {
          logger.push('Push non disponible ou refusé');
        }

        // 2. Initialiser l'écoute foreground (même si le token n'a pas été obtenu,
        //    car sur Android le listener foreground est indépendant)
        if (!cleanupForeground.current) {
          const cleanup = await initForegroundListener(handleForegroundMessage, navigate);
          if (!isCancelled && cleanup) {
            cleanupForeground.current = cleanup;
          }
        }
      } catch (error) {
        console.error('[usePushNotifications] Erreur initialisation:', error);
      }
    };

    initPush();

    // Cleanup au démontage
    return () => {
      isCancelled = true;
    };
  }, [isReadyToRegister, userId, residenceId, handleForegroundMessage]);

  // ============================================
  // EFFET — Nettoyage du listener foreground au démontage global
  // ============================================
  useEffect(() => {
    return () => {
      if (cleanupForeground.current) {
        cleanupForeground.current();
        cleanupForeground.current = null;
      }
    };
  }, []);

  // ============================================
  // EFFET — Désinscription au logout
  // ============================================
  useEffect(() => {
    // Si l'utilisateur se déconnecte, supprimer les tokens
    if (!isAuthenticated && isRegistered.current && userId) {
      unregisterPushNotifications(userId).catch((error) => {
        console.error('[usePushNotifications] Erreur désinscription:', error);
      });
      isRegistered.current = false;
      registeredTokenHash.current = null;
    }
  }, [isAuthenticated, userId]);

  // Ce hook ne retourne rien — il travaille en side-effect uniquement
};

export default usePushNotifications;
